import {
  FILTER_MATCH_TYPE_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import { SearchMatchType } from '@aibees/elephant/types';
import { TableProps } from '@aibees/shared/components';
import { Nil } from '@aibees/shared/definition';
import { concat, uniq } from 'lodash-es';
import {
  companyKeywordSourceExactMatchConfig,
  peopleKeywordSourceExactMatchConfig,
} from '../search-filters/config/keywords';
import { PeopleKeywordSources } from '../search-table-people/hooks/types';
import { config } from './components/relevancy-summary-popup/config';
import {
  InclusionQueries,
  InclusionQueryWithSeachMatchFields,
  KeywordSources,
  QueryTarget,
} from './hooks/types';
import { CompanyStaff } from './types';

export function getFullSelectedIds(
  idsFromTheApi: string[],
  table?: TableProps
) {
  // Maziar: I think its better to refactor the table selection logic so we can have single source of Ids for this API
  // TODO: if the sorting of the sended Ids is important, this logic should be enhanced a bit
  return uniq(
    concat(
      table?.selection.topSelected || [],
      table?.selection.added || [],
      (idsFromTheApi.length ? idsFromTheApi : table?.selection.included) || []
    ).filter((i) => !table?.selection.excluded.includes(i))
  );
}

export function getCompanySizeRange(staff: Nil<CompanyStaff>) {
  if (!staff || !staff.range) return 'N/A';
  let response = '';
  if (staff.range.start || staff.range.start === 0) {
    response += staff.range.start.toString();
  }
  if (staff.range.end) {
    response += '-';
    response += staff.range.end.toString();
  } else {
    response = `${response}+`;
  }

  return response;
}

export function handleSearchMatchInclusionQuery(
  name: keyof InclusionQueries,
  value: string,
  target: QueryTarget,
  field?: string,
  searchMatchValues?: string,
  defaultSearchMatchType?: SearchMatchType
) {
  const isFilterHasSearchMatch =
    Object.values(InclusionQueryWithSeachMatchFields).includes(
      name as InclusionQueryWithSeachMatchFields
    ) && !(name === 'language' && target === QueryTarget.account);

  const { searchMatchType } = extractFilterSearchMatchType(
    field,
    searchMatchValues,
    isFilterHasSearchMatch,
    defaultSearchMatchType
  );

  let valueDraft: string[] | { mode: SearchMatchType; content: string[] } =
    value.split(FILTER_VALUE_SEPARATOR);
  if (searchMatchType) {
    valueDraft = {
      mode: searchMatchType,
      content: value.split(FILTER_VALUE_SEPARATOR),
    };
  }

  return { searchMatchType, valueDraft };
}

export function extractFilterSearchMatchType(
  field?: string,
  searchMatchValues?: string,
  isFilterHasSearchMatch?: boolean,
  defaultSearchMatchType?: SearchMatchType
) {
  let searchMatchType: SearchMatchType | undefined;
  if (
    field === 'bulk_include_company_name_or_domain' ||
    field === 'bulk_exclude_company_name_or_domain'
  ) {
    searchMatchType = SearchMatchType.WORD;
    return { searchMatchType };
  }
  if (searchMatchValues && field && isFilterHasSearchMatch) {
    const draft = searchMatchValues.split(FILTER_VALUE_SEPARATOR);
    const fieldDraft = draft.find((x) => x.includes(field));
    if (fieldDraft) {
      searchMatchType = fieldDraft.split(
        FILTER_MATCH_TYPE_SEPARATOR
      )[1] as SearchMatchType;
    }
  }

  if (isFilterHasSearchMatch && !searchMatchType) {
    searchMatchType = defaultSearchMatchType ?? SearchMatchType.SMART;
  }

  return { searchMatchType };
}

export function extractSearchMatchValuesForKeywordSources(
  target: QueryTarget,
  sourceValue: string,
  sourceSearchMatchValues: string
) {
  const allSourceKeys =
    target === QueryTarget.account
      ? Object.values(KeywordSources)
      : Object.values(PeopleKeywordSources);
  const sourceKeys = sourceValue
    ? sourceValue.split(FILTER_VALUE_SEPARATOR)
    : allSourceKeys;
  const searchMatchParamsArray = sourceSearchMatchValues
    ? sourceSearchMatchValues.split(FILTER_VALUE_SEPARATOR)
    : [];
  const searchMatchValues = sourceKeys?.map((x) => {
    const source = x;
    const mode = searchMatchParamsArray
      .find((y) => x === y.split(FILTER_MATCH_TYPE_SEPARATOR)[0])
      ?.split(FILTER_MATCH_TYPE_SEPARATOR)[1] as SearchMatchType;

    const defaultSearchMatchType =
      target === QueryTarget.account
        ? companyKeywordSourceExactMatchConfig[source as KeywordSources].default
        : peopleKeywordSourceExactMatchConfig[
            source as keyof typeof peopleKeywordSourceExactMatchConfig
          ].default;

    return { mode: mode ?? defaultSearchMatchType, source };
  });

  return { searchMatchValues };
}

export function getRelevancyConfig(score: number) {
  if (score >= 90) return config[0]; // A grade
  if (score >= 70) return config[1]; // B grade
  if (score >= 50) return config[2]; // C grade
  return config[3]; // D grade
}
